import {createRouter, createWebHistory, RouteRecordRaw} from 'vue-router'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'

import RouteViewComponent from '../layouts/RouterBypass.vue'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: {name: 'dashboard'},
  },
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    redirect: {name: 'dashboard'},
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
      },
      {
        name: 'handler-companies',
        path: 'handler-companies',
        component: () => import('../pages/handler-companies/HandlerCompaniesPage.vue'),
      },
      {
        name: 'users',
        path: 'users',
        component: () => import('../pages/users/UsersPage.vue'),
      },
      {
        name: 'client-invoices',
        path: 'client-invoices',
        component: () => import('../pages/users/ClientInvoicePage.vue'),
      },
      {
        name: 'sent-emails',
        path: 'sent-emails',
        component: () => import('../pages/emails/SentEmailsPage.vue'),
      },
      {
        name: 'projects',
        path: 'projects',
        component: () => import('../pages/projects/ProjectsPage.vue'),
      },
      {
        name: 'payments',
        path: '/payments',
        component: RouteViewComponent,
        children: [
          {
            name: 'payment-methods',
            path: 'payment-methods',
            component: () => import('../pages/payments/PaymentsPage.vue'),
          },
          {
            name: 'billing',
            path: 'billing',
            component: () => import('../pages/billing/BillingPage.vue'),
          },
          {
            name: 'pricing-plans',
            path: 'pricing-plans',
            component: () => import('../pages/pricing-plans/PricingPlans.vue'),
          },
        ],
      },
      {
        name: 'faq',
        path: '/faq',
        component: () => import('../pages/faq/FaqPage.vue'),
      },
      {
        name: 'email-history',
        path: '/email-history',
        component: () => import('../pages/email-history/EmailHistoryPage.vue'),
      },
      {
        name: 'client-emails',
        path: '/client-emails',
        component: () => import('../pages/client-emails/ClientEmailsPage.vue'),
      },
      {
        name: 'xml-manager',
        path: 'xml-manager',
        component: () => import('../pages/xml-manager/XmlManagerPage.vue'),
      },
      {
        name: 'reports',
        path: 'reports',
        component: () => import('../pages/reports/ReportsPage.vue'),
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        path: '',
        redirect: {name: 'login'},
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
  {
    name: 'contract-sign',
    path: '/contract/:token',
    component: () => import('../pages/contract/ContractSignPage.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return {el: to.hash, behavior: 'smooth'}
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

// Auth guard
router.beforeEach(async (to, from, next) => {
  // Import here to avoid circular dependency
  const { useUserStore } = await import('../stores/user-store')
  const userStore = useUserStore()

  // Public routes that don't require authentication
  const publicRoutes = ['login', 'signup', 'recover-password', 'recover-password-email', 'contract-sign', '404']
  const isPublicRoute = publicRoutes.includes(to.name as string)

  // Check if user is authenticated
  const isAuthenticated = userStore.isAuthenticated

  if (!isAuthenticated && !isPublicRoute) {
    // Redirect to login if not authenticated
    next({ name: 'login', query: { redirect: to.fullPath } })
  } else if (isAuthenticated && to.name === 'login') {
    // Redirect to dashboard if already logged in and trying to access login
    next({ name: 'dashboard' })
  } else {
    // Proceed to route
    next()
  }
})

export default router
